import {
    IconButton,
    Loading,
    ModalWrapper,
    StyleFunction,
    Text,
    useModal,
    useThemedStyle,
} from "@venuepos/react-common";
import type {
    FunctionButtonInput,
    ILayoutProduct,
    FunctionButton,
    ProductButton,
    LayoutButtonMetaData,
    CmpEventButton,
    CmpEventProductButton,
    ProductSectionButton,
    ProductLayoutButtonType,
    CmpProductButton,
    LayoutEntityButton,
    MultiProductButton,
} from "lib";
import React, { useCallback, useState } from "react";
import { useTranslation } from "locales";
import { View } from "react-native";
import { Divider } from "react-native-paper";
import { ButtonTypeSelector } from "./button-type-selector";
import { FunctionButtonForm } from "./function-button/function-button-form";
import { ProductButtonMainForm } from "./product-button/product-button-main-form";
import { ProductSelector } from "./product-button/product-selector";
import { CmpEventForm } from "./cmp-event/cmp-event-form";
import { CmpEventProductForm } from "./cmp-event/cmp-event-product-form";
import { CmpProductForm } from "./cmp-event/cmp-product-form";
import { useIntegrationsQuery } from "graphql-sdk";
import { LayoutEntityForm } from "./layout-entity-form";
import { MultiProductForm } from "./multi-product-button/multi-product-form";
import { LayoutReferenceData } from "../../product-layout-operation-reducer";

export type CmpProductModalOutput = {
    buttonType: "CMP_PRODUCT";
    formValues: CmpProductButton;
    displayValues: { label: string; color: string; formattedAmount: string };
};

export type CmpEventModalOutput = {
    buttonType: "CMP_EVENT";
    formValues: CmpEventButton;
    displayValues: { label: string; color: string };
};

export type CmpEventProductModalOutput = {
    buttonType: "CMP_EVENT_PRODUCT";
    formValues: CmpEventProductButton;
    displayValues: { label: string; color: string; formattedAmount: string };
};

export type SingleProductModalOutput = {
    buttonType: "PRODUCT";
    formValues: ProductButton;
    productData: ILayoutProduct;
    displayValues: { label: string; color: string; formattedAmount: string };
};

export type FunctionButtonModalOutput = {
    buttonType: "FUNCTION";
    formValues: FunctionButton;
    displayValues?: { label: string; color: string; formattedAmount?: string }; // Only some of the types allow the color to be overridden, so displayValues is nullable.
};

export type LayoutEntityModalOutput = {
    buttonType: "LAYOUT_ENTITY";
    formValues: LayoutEntityButton;
    displayValues?: { label: string; color: string; formattedAmount?: string }; // Only some of the types allow the color to be overridden, so displayValues is nullable.
};

export type MultiProductModalOutput = {
    buttonType: "MULTI_PRODUCT";
    formValues: MultiProductButton;
    displayValues: { label: string; color: string; formattedAmount: string };
};

export type AddButtonModalOutput =
    | CmpProductModalOutput
    | CmpEventModalOutput
    | CmpEventProductModalOutput
    | SingleProductModalOutput
    | {
          // Add multiple selected products in one go.
          buttonType: "PRODUCT";
          formValues: null; // If the values are returned from the "select products" section, then there are no formValues
          productData: ILayoutProduct[];
      }
    | FunctionButtonModalOutput
    | LayoutEntityModalOutput
    | MultiProductModalOutput;

export type AddButtonModalProps = {
    button: Partial<ProductSectionButton>;

    buttonMetaData: LayoutButtonMetaData;

    // If a product has already been selected for this button, this argument is defined.
    product?: ILayoutProduct;

    // The set of products, that has already been added to this layout.
    addedProducts: ILayoutProduct["id"][];

    referenceProducts: LayoutReferenceData["products"];
    referenceEvents: LayoutReferenceData["events"];

    onClose: (value?: AddButtonModalOutput) => void;
};

export function AddButtonModal({
    button,
    buttonMetaData,
    product,
    addedProducts,
    referenceProducts,
    referenceEvents,
    onClose,
}: AddButtonModalProps) {
    const styles = useThemedStyle(styleFunc);
    const [t] = useTranslation();
    const { data: integrationsData, loading: integrationsLoading } =
        useIntegrationsQuery();

    const [buttonType, setButtonType] =
        useState<ProductLayoutButtonType | null>(button.buttonType || null);

    const [productId, setProductId] = useState<string>(
        (button as ProductButton).productId
            ? (button as ProductButton).productId
            : ""
    );
    const [buttonProduct, setButtonProduct] = useState<ILayoutProduct | null>(
        product || null
    );

    function resetButtonData() {
        setButtonProduct(null);
        setProductId(null);
        setButtonType(null);
    }

    function clearProduct() {
        setButtonProduct(null);
        setProductId(null);
    }

    function handleSelectProduct({
        selectedProduct,
    }: {
        selectedProduct: ILayoutProduct;
    }) {
        setProductId(selectedProduct.id);
        setButtonProduct(selectedProduct);
    }

    const handleAddProductsToLayout = useCallback(
        (selectedProducts: ILayoutProduct[]) => {
            onClose({
                buttonType: "PRODUCT",
                formValues: null,
                productData: selectedProducts,
            });
        },
        [onClose]
    );

    if (integrationsLoading) {
        return <Loading />;
    }

    return (
        <ModalWrapper
            title={
                button.buttonType
                    ? t("backoffice.layout.edit_button", "Edit button")
                    : t("backoffice.layout.add_button", "Add button")
            }
            onClose={() => onClose(undefined)}
            containerStyle={styles.modalContainer}
        >
            <View style={styles.metadataContainer}>
                <View style={styles.metadataContainerRow}>
                    <Text style={[styles.metadataText, styles.metadataHeader]}>
                        {`${t("backoffice.common.type", "Type")}: `}
                    </Text>
                    <Text
                        style={[
                            styles.metadataText,
                            buttonType ? null : styles.italic,
                        ]}
                    >
                        {buttonType
                            ? t(
                                  `backoffice.layout.button_type_${buttonType}`,
                                  buttonType
                              )
                            : t("backoffice.common.no_value", "No value")}
                    </Text>
                    {buttonType ? (
                        <IconButton
                            name="edit"
                            color={styles.editButton.color}
                            onPress={resetButtonData}
                            size="small"
                        />
                    ) : null}
                </View>
            </View>

            <Divider />

            {!buttonType ? (
                <View style={[styles.container, styles.step1Container]}>
                    <ButtonTypeSelector
                        hasCmpIntegration={
                            integrationsData?.integrations.data.some(
                                itr => itr.type === "CMP-PRODUCT"
                            ) || false
                        }
                        onButtonTypeSelect={setButtonType}
                    />
                </View>
            ) : (
                <View
                    style={[
                        styles.container,
                        buttonType === "PRODUCT"
                            ? styles.step2ProductContainer
                            : null,
                    ]}
                >
                    {buttonType === "PRODUCT" ? (
                        !productId ? (
                            // If no product has been selected yet, then present the ProductSelector
                            <ProductSelector
                                addedProducts={addedProducts}
                                onSelectProduct={handleSelectProduct}
                                onAddProductsToLayout={
                                    handleAddProductsToLayout
                                }
                            />
                        ) : (
                            // If a product has been selected, then present the Product button Main form
                            <ProductButtonMainForm
                                button={button as ProductButton}
                                buttonProduct={buttonProduct!}
                                onClose={onClose}
                                onClearProduct={clearProduct}
                                metaData={buttonMetaData}
                            />
                        )
                    ) : null}

                    {buttonType === "FUNCTION" ? (
                        <FunctionButtonForm
                            onSubmit={onClose}
                            values={button as FunctionButtonInput}
                            metaData={buttonMetaData}
                        />
                    ) : null}

                    {buttonType === "CMP_EVENT" ? (
                        <CmpEventForm
                            metaData={buttonMetaData}
                            values={button as CmpEventButton}
                            onSubmit={onClose}
                        />
                    ) : null}

                    {buttonType === "CMP_EVENT_PRODUCT" ? (
                        <CmpEventProductForm
                            metaData={buttonMetaData}
                            values={button as CmpEventProductButton}
                            onSubmit={onClose}
                        />
                    ) : null}

                    {buttonType === "CMP_PRODUCT" ? (
                        <CmpProductForm
                            metaData={buttonMetaData}
                            values={button as CmpProductButton}
                            onSubmit={onClose}
                        />
                    ) : null}

                    {buttonType === "LAYOUT_ENTITY" ? (
                        <LayoutEntityForm
                            metaData={buttonMetaData}
                            values={button as LayoutEntityButton}
                            onSubmit={onClose}
                        />
                    ) : null}

                    {buttonType === "MULTI_PRODUCT" ? (
                        <MultiProductForm
                            metaData={buttonMetaData}
                            values={button as MultiProductButton}
                            products={referenceProducts}
                            events={referenceEvents}
                            onSubmit={onClose}
                        />
                    ) : null}
                </View>
            )}
        </ModalWrapper>
    );
}

const styleFunc: StyleFunction = (theme, dimensions) => ({
    container: {
        padding: theme.spacingScale * 2,
        width: Math.min(dimensions.width - theme.spacingScale * 2, 800),
        minHeight: Math.min(dimensions.height - theme.spacingScale * 2, 650),

        gap: theme.spacingScale,
    },

    modalContainer: {
        minHeight: Math.min(dimensions.height - theme.spacingScale * 2, 300),
        maxHeight: dimensions.height * 0.9,
    },

    step1Container: {
        minHeight: Math.min(dimensions.height - theme.spacingScale * 2, 400),
    },

    step2ProductContainer: {
        height: Math.min(dimensions.height - theme.spacingScale * 2, 1000),
    },

    metadataContainer: {
        ...theme.styles.row,
        paddingHorizontal: theme.spacingScale * 2,
        marginBottom: theme.spacingScale,
        gap: theme.spacingScale,
    },

    metadataContainerRow: {
        ...theme.styles.flex1,
        ...theme.styles.row,

        alignItems: "center",
    },

    metadataText: {
        ...theme.styles.smallText,
        color: theme.colors.textDark,
    },

    metadataHeader: {
        fontWeight: "bold",
        color: theme.colors.grey500,
    },

    italic: {
        fontStyle: "italic",
    },

    editButton: {
        color: theme.colors.tertiary,
    },
});

export function useAddButtonModal() {
    const { render } = useModal<AddButtonModalOutput | undefined>();
    return async (props: Omit<AddButtonModalProps, "onClose">) =>
        render(onClose => <AddButtonModal {...props} onClose={onClose} />);
}
